import {
  DynamoDbLocationDetail,
  DynamoDbFODwellingRisk,
  DynamoDbFOPropertyStructuresRisk,
  DynamoDbFarmRiskDetail
} from '@ourbranch/schema-types/dynamodb';
import { FarmAddressDetails } from '@ourbranch/schema-types/staff-appsync';
import { FarmRiskType } from '@ourbranch/lookups';
import { policyTypes } from '@ourbranch/policy-types';

export const DWELLING_RISK_TYPES = [FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3];

// FARM_BASED_POLICY_TYPES / isFarmBasedPolicyType now live in the zero-dependency
// @ourbranch/policy-types package so every layer (FE, low-level packages) can import the
// canonical farm check without pulling in farm-utils' heavier schema-types dependency.
// Re-exported here for backwards compatibility with existing farm-utils importers.
export { FARM_BASED_POLICY_TYPES, isFarmBasedPolicyType } from '@ourbranch/policy-types';

// #32514: a Homeowners (Z) or Rental (Y) farm-based policy must insure a dwelling — you cannot
// insure only an FO-6 (other structures). Home accepts FO-1/2/3/4; Rental accepts FO-1/2/3 (Rental
// has no FO-4). Full Farm (F) and non-farm products have no dwelling requirement (a farm may insure
// only structures/equipment). Shared by the staff-fe validation schema and the Forreston backend
// underwriting rules so the eligibility check is identical on the FE agent flow and the backend.
export const HOME_DWELLING_RISK_TYPES: readonly string[] = [
  FarmRiskType.Fo1,
  FarmRiskType.Fo2,
  FarmRiskType.Fo3,
  FarmRiskType.Fo4
];
export const RENTAL_DWELLING_RISK_TYPES: readonly string[] = DWELLING_RISK_TYPES;

const REQUIRED_DWELLING_RISK_TYPES_BY_POLICY_TYPE: Record<string, readonly string[]> = {
  [policyTypes.FarmHome]: HOME_DWELLING_RISK_TYPES,
  [policyTypes.FarmRental]: RENTAL_DWELLING_RISK_TYPES
};

/**
 * Returns the dwelling risk types that satisfy a farm product's "must insure a dwelling" rule, or
 * null if the product has no such rule (full Farm, non-farm). (#32514)
 */
export function getRequiredDwellingRiskTypes(policyType: string | null | undefined): readonly string[] | null {
  if (!policyType) {
    return null;
  }
  return REQUIRED_DWELLING_RISK_TYPES_BY_POLICY_TYPE[policyType] ?? null;
}

/** Whether the given farm product must insure a dwelling (Home/Rental) at all. (#32514) */
export function farmPolicyRequiresDwelling(policyType: string | null | undefined): boolean {
  return getRequiredDwellingRiskTypes(policyType) !== null;
}

// Minimal structural shape needed to evaluate the dwelling requirement. Kept permissive so both the
// backend (DynamoDbLocationDetail[]) and the staff-fe form values (looser objects) can be passed
// without casts. (#32514)
interface DwellingRequirementLocation {
  isDeleted?: boolean | null;
  risks?: ({ type?: string | null } | null | undefined)[] | null;
}

/**
 * Whether a farm product's dwelling requirement is met — i.e. at least one non-deleted location
 * carries a qualifying dwelling risk. Products without a dwelling requirement (full Farm, non-farm)
 * always return true. (#32514)
 */
export function hasRequiredDwelling(
  policyType: string | null | undefined,
  farmLocations: readonly (DwellingRequirementLocation | null | undefined)[] | null | undefined
): boolean {
  const required = getRequiredDwellingRiskTypes(policyType);
  if (!required) {
    return true;
  }
  const requiredUpper = required.map((type) => type.toUpperCase());
  return (farmLocations || [])
    .filter((location): location is DwellingRequirementLocation => !!location && !location.isDeleted)
    .some((location) =>
      (location.risks || []).some((risk) => !!risk?.type && requiredUpper.includes(String(risk.type).toUpperCase()))
    );
}

/**
 * Flattens all risks from farm locations into a single array
 * @param farmLocations Array of farm locations
 * @returns Array of all risks from all locations, filtered for null/undefined values
 */
export function flattenFarmRisks(
  farmLocations: (DynamoDbLocationDetail | null | undefined)[]
): DynamoDbFarmRiskDetail[] {
  if (!farmLocations || !Array.isArray(farmLocations)) {
    return [];
  }

  return farmLocations
    .filter((location): location is DynamoDbLocationDetail => location !== null)
    .flatMap((location) => (location.risks?.filter(Boolean) || []) as DynamoDbFarmRiskDetail[]);
}

/**
 * Filters risks to only include dwelling risks (FO1, FO2, FO3)
 * @param risks Array of farm risks
 * @returns Array of dwelling risks only
 */
export function filterDwellingRisks(risks: DynamoDbFarmRiskDetail[]): DynamoDbFODwellingRisk[] {
  return risks.filter(
    (risk): risk is DynamoDbFODwellingRisk =>
      !!risk &&
      !!risk.type &&
      (risk.type === FarmRiskType.Fo1 ||
        risk.type === FarmRiskType.Fo2 ||
        risk.type === FarmRiskType.Fo3 ||
        DWELLING_RISK_TYPES.includes(risk.type.toUpperCase()))
  );
}

/**
 * Filters risks to only include property structures risks (FO6)
 * @param risks Array of farm risks
 * @returns Array of property structures risks only
 */
export function filterPropertyStructureRisks(risks: DynamoDbFarmRiskDetail[]): DynamoDbFOPropertyStructuresRisk[] {
  return risks.filter(
    (risk): risk is DynamoDbFOPropertyStructuresRisk =>
      !!risk && !!risk.type && risk.type === FarmRiskType.Fo6Structures && 'coverages' in risk
  );
}

/**
 * Gets dwelling risks from farm locations in a single operation
 * @param farmLocations Array of farm locations
 * @returns Array of dwelling risks only
 */
export function getDwellingRisksFromLocations(
  farmLocations: (DynamoDbLocationDetail | null | undefined)[]
): DynamoDbFODwellingRisk[] {
  const allRisks = flattenFarmRisks(farmLocations as DynamoDbLocationDetail[]);
  return filterDwellingRisks(allRisks);
}

/**
 * Gets property structures risks from farm locations in a single operation
 * @param farmLocations Array of farm locations
 * @returns Array of property structures risks only
 */
export function getPropertyStructureRisksFromLocations(
  farmLocations: (DynamoDbLocationDetail | null | undefined)[]
): DynamoDbFOPropertyStructuresRisk[] {
  const allRisks = flattenFarmRisks(farmLocations as DynamoDbLocationDetail[]);
  return filterPropertyStructureRisks(allRisks);
}

/**
 * Checks if a risk is a dwelling risk type
 * @param riskType The risk type to check
 * @returns True if the risk is a dwelling type (FO1, FO2, or FO3)
 */
export function isDwellingRiskType(riskType: string): boolean {
  if (!riskType) {
    return false;
  }

  const upperType = riskType.toUpperCase();
  return (
    upperType === FarmRiskType.Fo1 ||
    upperType === FarmRiskType.Fo2 ||
    upperType === FarmRiskType.Fo3 ||
    DWELLING_RISK_TYPES.includes(upperType)
  );
}

/**
 * Checks if a risk is a renters risk type
 * @param riskType The risk type to check
 * @returns True if the risk is a renters type (FO4)
 */
export function isRentersRiskType(riskType: string): boolean {
  if (!riskType) {
    return false;
  }

  const upperType = riskType.toUpperCase();
  return upperType === FarmRiskType.Fo4;
}

/**
 * Checks if a risk is a property risk type
 * @param riskType The risk type to check
 * @returns True if the risk is a property type (FO6)
 */
export function isPropertyRiskType(riskType: string): boolean {
  if (!riskType) {
    return false;
  }

  const upperType = riskType.toUpperCase();
  return (
    upperType === FarmRiskType.Fo6Structures.toUpperCase() ||
    upperType === FarmRiskType.Fo6Scheduled.toUpperCase() ||
    upperType === FarmRiskType.Fo6Unscheduled.toUpperCase()
  );
}

/**
 * Checks if a risk is a dwelling (FO1/FO2/FO3) or a farm structure (FO6Structures).
 *
 * These are the only risk additions that should re-trigger underwriting questions on a
 * farm endorsement (issue #31439). Adding scheduled property (FO6Scheduled), unscheduled
 * property (FO6Unscheduled), or an FO4 renters dwelling does NOT require new UW answers.
 * @param riskType The risk type to check
 * @returns True if the risk is a dwelling or farm structure
 */
export function isDwellingOrStructureRiskType(riskType: string): boolean {
  if (!riskType) {
    return false;
  }
  return isDwellingRiskType(riskType) || riskType.toUpperCase() === FarmRiskType.Fo6Structures.toUpperCase();
}

/**
 * Farm risk "form families" — groups of risk types that share the same building + coverage shape in
 * the staff-fe risk detail forms. Switching a risk BETWEEN families invalidates the previously
 * entered building/coverage data (the field sets barely overlap), so that data must be reset;
 * otherwise incompatible fields (e.g. FO-6 agricultural buildingType/occupancy or coverage E) leak
 * into the dwelling rater and produce a "farm rating error". Switching WITHIN the dwelling family
 * (FO-1 ↔ FO-2 ↔ FO-3) preserves the data. (#32514 reopen)
 */
export const FarmRiskFamily = {
  Dwelling: 'dwelling', // FO-1 / FO-2 / FO-3 — dwelling building + A/B/C/D coverages
  Renters: 'renters', // FO-4 — contents-only renters
  Structures: 'structures', // FO-6 other structures — agricultural building + coverage E
  Scheduled: 'scheduled', // FO-6 scheduled farm property
  Unscheduled: 'unscheduled' // FO-6 unscheduled farm property
} as const;

export type FarmRiskFamilyValue = (typeof FarmRiskFamily)[keyof typeof FarmRiskFamily];

/**
 * Classifies a farm risk type into its form family, or null if the type is missing/unrecognized.
 * Case-insensitive. (#32514 reopen)
 */
export function getFarmRiskFamily(riskType: string | null | undefined): FarmRiskFamilyValue | null {
  if (!riskType) {
    return null;
  }
  if (isDwellingRiskType(riskType)) {
    return FarmRiskFamily.Dwelling;
  }
  if (isRentersRiskType(riskType)) {
    return FarmRiskFamily.Renters;
  }
  const upperType = riskType.toUpperCase();
  if (upperType === FarmRiskType.Fo6Structures.toUpperCase()) {
    return FarmRiskFamily.Structures;
  }
  if (upperType === FarmRiskType.Fo6Scheduled.toUpperCase()) {
    return FarmRiskFamily.Scheduled;
  }
  if (upperType === FarmRiskType.Fo6Unscheduled.toUpperCase()) {
    return FarmRiskFamily.Unscheduled;
  }
  return null;
}

/**
 * Whether changing a farm risk's type from `fromType` to `toType` crosses a form-family boundary —
 * meaning the previously-entered building + coverage data no longer applies and must be reset to a
 * clean slate. Returns false when either type is missing/unrecognized (no safe reset decision) or
 * when both belong to the same family (e.g. FO-1 → FO-3, which share a building shape). (#32514 reopen)
 */
export function isCrossFamilyFarmRiskChange(
  fromType: string | null | undefined,
  toType: string | null | undefined
): boolean {
  const fromFamily = getFarmRiskFamily(fromType);
  const toFamily = getFarmRiskFamily(toType);
  if (!fromFamily || !toFamily) {
    return false;
  }
  return fromFamily !== toFamily;
}

export interface FarmAddressEntity {
  id: string;
  type: string;
  address: FarmAddressDetails;
}

/**
 * Extracts all unique addresses from farm locations
 * @param farmLocations Array of farm locations
 * @returns Array of address entities with id, type, and address, deduplicated by address
 */
export function getAllFarmAddressEntities(
  farmLocations?: (DynamoDbLocationDetail | null | undefined)[]
): FarmAddressEntity[] {
  const entities: FarmAddressEntity[] = [];

  if (!farmLocations || !Array.isArray(farmLocations)) {
    return entities;
  }

  farmLocations.forEach((location) => {
    if (!location) {
      return;
    }

    // Location address only - risks now use their parent location's address
    if (location.address) {
      entities.push({
        id: location.id,
        type: 'location',
        address: location.address
      });
    }
  });

  // Deduplicate by address (simple string comparison)
  const seen = new Set<string>();
  return entities.filter((entity) => {
    const addressKey = `${entity.address.address}_${entity.address.city}_${entity.address.state}_${entity.address.zip}`;
    if (seen.has(addressKey)) {
      return false;
    }
    seen.add(addressKey);
    return true;
  });
}

/**
 * Custom error for farm state validation failures
 */
export class FarmStateError extends Error {
  constructor(
    message: string,
    public readonly farmLocations?: any[]
  ) {
    super(message);
    this.name = 'FarmStateError';
  }
}

/**
 * Gets the primary state for farm operations - STRICT VERSION
 * Throws error if primary farm location doesn't have a state
 * For farm insurance, state MUST come from primary location only
 * @param farmLocations Array of farm locations
 * @returns Primary farm location state
 * @throws {FarmStateError} When farm location data is invalid or missing state
 */
export function getFarmPrimaryStateStrict(farmLocations?: any[]): string {
  if (!farmLocations || !Array.isArray(farmLocations) || farmLocations.length === 0) {
    throw new FarmStateError('No farm locations provided for state determination');
  }

  const primaryLocation = farmLocations[0];
  if (!primaryLocation?.address) {
    throw new FarmStateError('Primary farm location has no address', farmLocations);
  }

  const primaryState = primaryLocation.address.state;
  if (!primaryState || primaryState.trim() === '') {
    throw new FarmStateError('Primary farm location has no state', farmLocations);
  }

  return primaryState.trim();
}

/**
 * Determines if a quote/policy is a farm policy based on farmLocations presence
 * @param quote The quote or policy object
 * @returns True if the object has farmLocations with at least one location
 */
export function isFarmPolicy(quote: any): boolean {
  return quote?.farmLocations && Array.isArray(quote.farmLocations) && quote.farmLocations.length > 0;
}

/**
 * Normalizes county names by removing " COUNTY" suffix and converting to uppercase
 * Used for consistent county name formatting across mine subsidence defaults and other county-based lookups
 * @param county The county name to normalize (e.g., "Bond County" or "BOND COUNTY")
 * @returns Normalized county name in uppercase without suffix (e.g., "BOND"), or empty string if null/undefined
 */
export function normalizeCountyName(county: string | null | undefined): string {
  return county?.replace(/ COUNTY/i, '').toUpperCase() ?? '';
}

/**
 * Occupancy codes for Silos/Grain Bins (SG building type) that use Length × Width
 * dimensions instead of Diameter for sqFt calculation and Verisk 360Value submissions.
 * CC = Corn Crib, GS = Commodity Shed, GH = Grain/Feed Handling Center
 * GitHub Issue: #29355
 */
export const LENGTH_WIDTH_OCCUPANCIES: readonly string[] = ['CC', 'GS', 'GH'];

/**
 * Checks if an occupancy type uses length and width instead of diameter.
 * @param occupancy - The occupancy code
 * @returns True if occupancy uses length/width dimensions
 */
export function usesLengthWidth(occupancy: string | null | undefined): boolean {
  if (!occupancy) {
    return false;
  }
  return LENGTH_WIDTH_OCCUPANCIES.includes(occupancy);
}

/**
 * Resolves a deductible value to a dollar amount.
 *
 * Forreston brand in Illinois stores deductibles as either percentage values (< 1, e.g. 0.01 = 1%)
 * or dollar amounts (>= 1). This function normalises both forms to a dollar amount so that the
 * backend rater and the frontend useEffect use identical resolution logic and never drift apart.
 *
 * @param deductibleValue - The raw deductible value (percentage < 1 or dollar amount >= 1)
 * @param coverageLimit - The coverage limit used to calculate the dollar amount for percentage values
 * @returns The resolved dollar amount, or 0 when inputs are missing
 */
export function resolveDeductibleAmount(deductibleValue: number | null | undefined, coverageLimit: number): number {
  // No deductible value → return 0
  if (deductibleValue == null) {
    return 0;
  }

  // No coverage limit → can't resolve a percentage, return original value
  if (!coverageLimit) {
    return deductibleValue;
  }

  // Values < 1 are percentages (e.g., 0.01 = 1%); >= 1 are already dollar amounts
  if (deductibleValue < 1) {
    return Math.round(coverageLimit * deductibleValue);
  }

  return deductibleValue;
}

// Re-export farm policy utilities
export { makePolicyDetailsFromSegments } from './farm-policy-utils';

// Re-export farm premium breakdown hierarchy
export { buildFarmPremiumBreakdownHierarchy } from './farm-premium-breakdown';

// Re-export Coverage C default/rule helpers (GitHub issue #29084)
export {
  getDefaultFarmCoverageC,
  resolveFarmCoverageCRules,
  type FarmCoverageCRules,
  type FarmCoverageCBusinessRules
} from './coverage-c-defaults';

// Re-export Coverage D default/rule helpers (GitHub issue #30548)
export {
  getDefaultFarmCoverageD,
  resolveFarmCoverageDRules,
  type FarmCoverageDRules,
  type FarmCoverageDBusinessRules
} from './coverage-d-defaults';

// Filed-form edition resolver for the Forreston Farm policy packet
export {
  FARM_FORM_EDITIONS,
  resolveFormEdition,
  type FarmFormEditionMap,
  type FormEdition
} from './farm-forms-editions';

// Signature-required filed forms + overlay coordinates (issue #30491)
export {
  SIGNATURE_REQUIRED_FARM_FORMS,
  FARM_FORM_SIGNATURE_COORDS,
  getFarmFormSignatureLayout,
  type SignatureCoord,
  type RestrictionTextCoord,
  type FarmFormSignatureLayout,
  type FarmFormSignatureCoords
} from './signature-required-forms';

// Application-packet notices — signed acknowledgments appended to the application
// packet that are NOT filed forms or endorsements (issue #30742).
export {
  FARM_APPLICATION_NOTICES,
  type FarmApplicationNotice,
  type FarmApplicationNoticeLayout,
  type FarmApplicationNoticeId
} from './farm-application-notices';

// Farm interested-party description-label constants (issue #30591) —
// shared between staff-fe and docs-engine farm-data-mapper.
export {
  MANAGER_OR_LESSOR_RELATIONSHIPS,
  DESIGNATED_PREMISES_LABEL,
  getManagerOrLessorDescriptionLabel
} from './farm-interested-party-labels';

export { scrubFarmPartyAssociatedEntities } from './scrub-farm-party-entities';

// Issue #30825: shared empty-shape factory for farmLiabilityCoverage. Consumed by
// staff-fe (eager UX clearing) and the forreston post-data-rules (defensive backstop)
// so the two layers can't drift as fields are added. The default-builder repopulates
// Coverage L / M / aggregate when the user toggles Liability ON or switches
// Personal↔Commercial (#30825 follow-up).
export {
  FARM_LIABILITY_DEFAULTS,
  buildDefaultFarmLiabilityCoverage,
  buildEmptyFarmLiabilityCoverage
} from './empty-farm-liability';
